import React, { useState, useEffect } from 'react';
import { FaSync, FaEye, FaEyeSlash, FaCheck, FaTimes, FaExclamationTriangle, FaLink, FaKey, FaCog, FaShieldAlt, FaPercent } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import toastService from '@/utils/toastService';

/**
 * Configuración exclusiva para el proveedor teknoParts
 * Maneja la integración API y configuraciones del acuerdo de exclusividad
 */
const ConfiguracionTeknoParts = () => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [descuentoGlobal, setDescuentoGlobal] = useState(10); // % descuento por defecto

  // Datos del proveedor exclusivo teknoParts
  const proveedorData = {
    id: 1,
    nombre: 'teknoParts',
    descripcion: 'Proveedor exclusivo de repuestos automotrices premium',
    logo: '/img/marketplace/teknoparts.png',
    activo: true,
    configuracion_api: {
      api_url: 'https://api.teknoparts.cl/v1',
      api_key_visible: 'tkp_live_ABC123...', // Parcialmente oculta
      api_key_encrypted: 'encrypted_key_hash_here',
      webhook_url: 'https://tu-app.com/webhooks/teknoparts',
      timeout: 30000,
      rate_limit: 150,
      version: 'v1'
    },
    acuerdo_exclusividad: {
      fecha_inicio: '2025-01-01T00:00:00Z',
      fecha_fin: '2025-12-31T23:59:59Z',
      comision_porcentaje: 5.0,
      volumen_minimo_mensual: 50, // órdenes
      descuento_volumen: 2.5, // % adicional si se supera el volumen
      territorio_exclusivo: 'Chile - Región Metropolitana'
    },
    metodos_pago: ['shopify', 'api_directa'],
    ultima_sincronizacion: '2025-01-28T10:30:00Z',
    estado_conexion: 'conectado',
    estadisticas: {
      ordenes_totales: 78,
      ordenes_exitosas: 75,
      ordenes_fallidas: 3,
      tiempo_respuesta_promedio: 750, // ms
      disponibilidad: 96.2, // %
      volumen_mes_actual: 23
    },
    contacto: {
      email: 'integracion@teknoparts.cl',
      telefono: '+56 2 2456 7890',
      soporte_tecnico: 'soporte@teknoparts.cl',
      gerente_cuentas: 'cuentas@teknoparts.cl'
    },
    fecha_creacion: '2025-01-01T00:00:00Z',
    fecha_actualizacion: '2025-01-28T10:30:00Z'
  };

  const sincronizarConTeknoParts = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toastService.success('Sincronización completada con teknoParts');
    } catch (error) {
      toastService.error('Error en sincronización con teknoParts');
    } finally {
      setLoading(false);
    }
  };

  const testearConexion = async () => {
    setTestLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const mockResults = {
        conexion_api: { status: 'success', tiempo: 742, mensaje: 'Conexión exitosa' },
        autenticacion: { status: 'success', tiempo: 234, mensaje: 'API Key válida' },
        catalogo: { status: 'success', tiempo: 1250, mensaje: '1,247 productos sincronizados' },
        inventario: { status: 'success', tiempo: 890, mensaje: 'Stock actualizado correctamente' },
        webhooks: { status: 'warning', tiempo: 2100, mensaje: 'Webhook configurado, pendiente de prueba' }
      };
      
      setTestResults(mockResults);
      setShowTestModal(true);
      toastService.success('Test de conexión completado');
      
    } catch (error) {
      toastService.error('Error en test de conexión');
    } finally {
      setTestLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'conectado':
        return <Badge variant="success" size="sm"><FaCheck className="mr-1" />Conectado</Badge>;
      case 'warning':
        return <Badge variant="warning" size="sm"><FaExclamationTriangle className="mr-1" />Advertencia</Badge>;
      case 'desconectado':
        return <Badge variant="danger" size="sm"><FaTimes className="mr-1" />Desconectado</Badge>;
      default:
        return <Badge variant="secondary" size="sm">Desconocido</Badge>;
    }
  };

  const diasRestantesExclusividad = () => {
    const fechaFin = new Date(proveedorData.acuerdo_exclusividad.fecha_fin);
    const hoy = new Date();
    const diferencia = fechaFin - hoy;
    return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
  };

  const porcentajeVolumen = () => {
    const actual = proveedorData.estadisticas.volumen_mes_actual;
    const minimo = proveedorData.acuerdo_exclusividad.volumen_minimo_mensual;
    return Math.min((actual / minimo) * 100, 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
        <div className="flex items-center">
          <div className="w-1 h-6 rounded-full mr-3 bg-purple-500" />
          <h1 className="text-xl sm:text-2xl font-bold">Configuración teknoParts</h1>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <Button
            variant="secondary"
            onClick={testearConexion}
            loading={testLoading}
            icon={<FaLink />}
            className="flex-1 sm:flex-none"
          >
            <span className="hidden sm:inline">Test Conexión</span>
            <span className="sm:hidden">Test</span>
          </Button>
          <Button
            variant="primary"
            onClick={sincronizarConTeknoParts}
            loading={loading}
            icon={<FaSync />}
            className="flex-1 sm:flex-none"
          >
            <span className="hidden sm:inline">Sincronizar</span>
            <span className="sm:hidden">Sync</span>
          </Button>
        </div>
      </div>


      {/* Información del Proveedor */}
      <Card>
        <CardBody className="p-0">
          {/* Header de la tarjeta */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-lg mr-3 flex items-center justify-center">
                  <span className="text-lg font-bold text-white">TK</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{proveedorData.nombre}</h3>
                  <p className="text-sm text-gray-400">{proveedorData.descripcion}</p>
                </div>
              </div>
              {getEstadoBadge(proveedorData.estado_conexion)}
            </div>
          </div>

          {/* Stats compactas en una fila */}
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="text-center bg-green-900/20 border border-green-800/50 rounded-lg p-3">
                <p className="text-xl font-bold text-green-400">{proveedorData.estadisticas.ordenes_exitosas}</p>
                <p className="text-xs text-green-300">Órdenes Exitosas</p>
              </div>
              
              <div className="text-center bg-blue-900/20 border border-blue-800/50 rounded-lg p-3">
                <p className="text-xl font-bold text-blue-400">{proveedorData.estadisticas.tiempo_respuesta_promedio}ms</p>
                <p className="text-xs text-blue-300">Tiempo Respuesta</p>
              </div>
              
              <div className="text-center bg-purple-900/20 border border-purple-800/50 rounded-lg p-3">
                <p className="text-xl font-bold text-purple-400">{proveedorData.estadisticas.disponibilidad}%</p>
                <p className="text-xs text-purple-300">Disponibilidad</p>
              </div>
              
              <div className="text-center bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-3">
                <p className="text-xl font-bold text-yellow-400">{proveedorData.estadisticas.ordenes_fallidas}</p>
                <p className="text-xs text-yellow-300">Órdenes Fallidas</p>
              </div>
            </div>

            {/* Configuración de descuento */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Configuración de Precios</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Descuento Global (%)</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={descuentoGlobal}
                      onChange={(e) => setDescuentoGlobal(Number(e.target.value))}
                      className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-l text-sm"
                    />
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="rounded-l-none"
                      onClick={() => toastService.success('Descuento actualizado para todos los productos')}
                    >
                      <FaCog />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Aplicado a todos los repuestos teknoParts</p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Comisión Marketplace (%)</label>
                  <input
                    type="text"
                    value={proveedorData.acuerdo_exclusividad.comision_porcentaje + '%'}
                    readOnly
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Definido en acuerdo de exclusividad</p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Margen Total Estimado</label>
                  <div className="px-3 py-2 bg-gray-900/50 border border-gray-600 rounded text-sm">
                    <span className="text-green-400 font-medium">
                      {(descuentoGlobal + proveedorData.acuerdo_exclusividad.comision_porcentaje).toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Descuento + comisión total</p>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Configuración API */}
      <Card>
        <CardBody className="p-6">
          <h3 className="text-lg font-semibold mb-4">Configuración API</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL API</label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={proveedorData.configuracion_api.api_url}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-l text-sm"
                  />
                  <Button variant="secondary" size="sm" className="rounded-l-none">
                    <FaLink />
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
                <div className="flex items-center">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={showApiKey ? "tkp_live_ABC123XYZ789..." : proveedorData.configuracion_api.api_key_visible}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-l text-sm"
                  />
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="rounded-l-none"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Timeout (ms)</label>
                <input
                  type="number"
                  value={proveedorData.configuracion_api.timeout}
                  readOnly
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Rate Limit</label>
                <input
                  type="number"
                  value={proveedorData.configuracion_api.rate_limit}
                  readOnly
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Versión API</label>
                <input
                  type="text"
                  value={proveedorData.configuracion_api.version}
                  readOnly
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Webhook URL</label>
              <input
                type="text"
                value={proveedorData.configuracion_api.webhook_url}
                readOnly
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm"
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Información de Contacto */}
      <Card>
        <CardBody className="p-6">
          <h3 className="text-lg font-semibold mb-4">Contactos teknoParts</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-300 mb-3">Soporte Técnico</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-400">Email:</span> {proveedorData.contacto.soporte_tecnico}</p>
                <p><span className="text-gray-400">Teléfono:</span> {proveedorData.contacto.telefono}</p>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-300 mb-3">Gerente de Cuentas</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-400">Email:</span> {proveedorData.contacto.gerente_cuentas}</p>
                <p><span className="text-gray-400">Integración:</span> {proveedorData.contacto.email}</p>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Modal de Test de Conexión */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title="Resultados del Test de Conexión"
        size="lg"
      >
        {testResults && (
          <div className="space-y-4">
            {Object.entries(testResults).map(([key, result]) => (
              <div key={key} className="flex items-center justify-between p-3 bg-gray-800 rounded">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    result.status === 'success' ? 'bg-green-500' :
                    result.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <div>
                    <p className="font-medium capitalize">{key.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-400">{result.mensaje}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">{result.tiempo}ms</span>
              </div>
            ))}
            
            <div className="mt-6">
              <Button 
                variant="primary" 
                onClick={() => setShowTestModal(false)}
                fullWidth
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ConfiguracionTeknoParts;